/**
 * Report Issue Section Component
 *
 * Allows users to report bugs, request features, or ask for support.
 * Visible to all authenticated users in the Settings > Personal tab.
 */

import React, { useState, useRef } from 'react';
import {
  AlertCircle,
  Bug,
  Lightbulb,
  HelpCircle,
  MessageCircle,
  Upload,
  X,
  CheckCircle,
  Loader2,
  Image,
} from 'lucide-react';
import { useTranslation } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import {
  createSupportRequest,
  getBrowserInfo,
  uploadImage,
} from '@business-assistant/shared';
import type {
  SupportRequestType,
  SupportPriority,
} from '@business-assistant/shared';

// =============================================================================
// Types
// =============================================================================

interface FormState {
  type: SupportRequestType;
  priority: SupportPriority;
  subject: string;
  description: string;
  attachmentUrl?: string;
}

// =============================================================================
// Component
// =============================================================================

interface ReportIssueSectionProps {
  onSuccess?: () => void;
}

export function ReportIssueSection({ onSuccess }: ReportIssueSectionProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formState, setFormState] = useState<FormState>({
    type: 'bug',
    priority: 'medium',
    subject: '',
    description: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const typeOptions: { value: SupportRequestType; icon: React.ReactNode; label: string; hint: string }[] = [
    { value: 'bug', icon: <Bug size={18} />, label: t('support.types.bug'), hint: t('support.typeHints.bug') },
    { value: 'feature', icon: <Lightbulb size={18} />, label: t('support.types.feature'), hint: t('support.typeHints.feature') },
    { value: 'support', icon: <HelpCircle size={18} />, label: t('support.types.support'), hint: t('support.typeHints.support') },
    { value: 'question', icon: <MessageCircle size={18} />, label: t('support.types.question'), hint: t('support.typeHints.question') },
  ];

  const priorityOptions: { value: SupportPriority; label: string; hint: string; color: string }[] = [
    { value: 'low', label: t('support.priorities.low'), hint: t('support.priorityHints.low'), color: 'var(--color-text-secondary)' },
    { value: 'medium', label: t('support.priorities.medium'), hint: t('support.priorityHints.medium'), color: 'var(--color-primary)' },
    { value: 'high', label: t('support.priorities.high'), hint: t('support.priorityHints.high'), color: 'var(--color-warning, #f59e0b)' },
    { value: 'urgent', label: t('support.priorities.urgent'), hint: t('support.priorityHints.urgent'), color: 'var(--color-error, #ef4444)' },
  ];

  const handleTypeSelect = (type: SupportRequestType) => {
    setFormState((prev) => ({ ...prev, type }));
  };

  const handlePrioritySelect = (priority: SupportPriority) => {
    setFormState((prev) => ({ ...prev, priority }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setAttachmentPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setIsUploadingImage(true);
    try {
      const uploaded = await uploadImage(file, 'support_attachment');
      setFormState((prev) => ({ ...prev, attachmentUrl: uploaded.url }));
    } catch (error) {
      console.error('Failed to upload image:', error);
      setAttachmentPreview(null);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveAttachment = () => {
    setAttachmentPreview(null);
    setFormState((prev) => ({ ...prev, attachmentUrl: undefined }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!formState.subject.trim() || !formState.description.trim()) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await createSupportRequest({
        type: formState.type,
        priority: formState.priority,
        subject: formState.subject.trim(),
        description: formState.description.trim(),
        attachmentUrl: formState.attachmentUrl,
        browserInfo: getBrowserInfo(),
        currentUrl: window.location.href,
      });

      setSubmitSuccess(true);

      // Notify parent to refresh the list
      onSuccess?.();

      // Reset form after success
      setTimeout(() => {
        setFormState({
          type: 'bug',
          priority: 'medium',
          subject: '',
          description: '',
        });
        setAttachmentPreview(null);
        setSubmitSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to submit support request:', error);
      setSubmitError(t('support.reportIssue.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="settings-panel">
        <div className="settings-panel-header">
          <AlertCircle size={18} />
          <span>{t('support.reportIssue.title')}</span>
        </div>
        <div className="settings-panel-content" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <CheckCircle size={48} style={{ color: 'var(--color-success, #10b981)', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px 0', color: 'var(--color-success, #10b981)' }}>
            {t('support.reportIssue.success')}
          </h3>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            We'll get back to you as soon as possible.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-panel">
      <div className="settings-panel-header">
        <AlertCircle size={18} />
        <span>{t('support.reportIssue.title')}</span>
      </div>
      <div className="settings-panel-content">
        <p className="settings-hint" style={{ marginBottom: '1.5rem' }}>
          {t('support.reportIssue.subtitle')}
        </p>

        {/* Request Type */}
        <div className="settings-field">
          <label className="settings-label">{t('support.reportIssue.type')}</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
            {typeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleTypeSelect(option.value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  border: formState.type === option.value
                    ? '2px solid var(--color-primary)'
                    : '1px solid var(--color-border)',
                  borderRadius: '8px',
                  background: formState.type === option.value
                    ? 'var(--color-primary-light, rgba(59, 130, 246, 0.1))'
                    : 'var(--color-bg-secondary)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{
                  color: formState.type === option.value
                    ? 'var(--color-primary)'
                    : 'var(--color-text-secondary)'
                }}>
                  {option.icon}
                </span>
                <div>
                  <div style={{
                    fontWeight: 500,
                    color: formState.type === option.value
                      ? 'var(--color-primary)'
                      : 'var(--color-text)'
                  }}>
                    {option.label}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                    {option.hint}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Priority */}
        <div className="settings-field" style={{ marginTop: '16px' }}>
          <label className="settings-label">{t('support.reportIssue.priority')}</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {priorityOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handlePrioritySelect(option.value)}
                style={{
                  padding: '8px 16px',
                  border: formState.priority === option.value
                    ? `2px solid ${option.color}`
                    : '1px solid var(--color-border)',
                  borderRadius: '20px',
                  background: formState.priority === option.value
                    ? `${option.color}20`
                    : 'var(--color-bg-secondary)',
                  color: formState.priority === option.value ? option.color : 'var(--color-text)',
                  cursor: 'pointer',
                  fontWeight: formState.priority === option.value ? 600 : 400,
                }}
                title={option.hint}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div className="settings-field" style={{ marginTop: '16px' }}>
          <label className="settings-label">{t('support.reportIssue.subject')} *</label>
          <input
            type="text"
            className="settings-input"
            value={formState.subject}
            onChange={(e) => setFormState((prev) => ({ ...prev, subject: e.target.value }))}
            placeholder={t('support.reportIssue.subjectPlaceholder')}
            maxLength={200}
          />
        </div>

        {/* Description */}
        <div className="settings-field" style={{ marginTop: '16px' }}>
          <label className="settings-label">{t('support.reportIssue.description')} *</label>
          <textarea
            className="settings-input"
            value={formState.description}
            onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
            placeholder={t('support.reportIssue.descriptionPlaceholder')}
            rows={5}
            style={{ resize: 'vertical', minHeight: '100px' }}
          />
        </div>

        {/* Attachment */}
        <div className="settings-field" style={{ marginTop: '16px' }}>
          <label className="settings-label">{t('support.reportIssue.attachment')}</label>
          <p className="settings-hint" style={{ marginBottom: '8px' }}>
            {t('support.reportIssue.attachmentHint')}
          </p>

          {attachmentPreview ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={attachmentPreview}
                alt="Attachment preview"
                style={{
                  maxWidth: '200px',
                  maxHeight: '150px',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                }}
              />
              <button
                type="button"
                onClick={handleRemoveAttachment}
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'var(--color-error, #ef4444)',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X size={14} />
              </button>
              {isUploadingImage && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                }}>
                  <Loader2 size={24} className="spinner" style={{ color: 'white' }} />
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="btn-secondary btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Image size={16} />
              {t('support.reportIssue.uploadImage')}
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
        </div>

        {/* Error Message */}
        {submitError && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: 'var(--color-error-light, rgba(239, 68, 68, 0.1))',
            color: 'var(--color-error, #ef4444)',
            borderRadius: '8px',
            fontSize: '14px',
          }}>
            {submitError}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !formState.subject.trim() || !formState.description.trim()}
          className="btn-primary"
          style={{ marginTop: '24px', width: '100%' }}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={16} className="spinner" />
              {t('support.reportIssue.submitting')}
            </>
          ) : (
            t('support.reportIssue.submit')
          )}
        </button>
      </div>
    </div>
  );
}

export default ReportIssueSection;
